package com.arena_web.ui.components.overlay

import androidx.compose.runtime.*
import com.arena_web.data.dto.UserCardDto
import com.arena_web.data.dto.UserStoryDto
import com.arena_web.ui.components.cards.PlayerCard
import com.arena_web.ui.components.cards.PlayerCardSize
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.components.widgets.GradientBorderBox
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.document
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.*
import org.w3c.dom.events.Event

// Figma 8672:444857 — story-viewer popup opened from a profile story circle.
// Outer surface is the same #09142d used by other profile modals; the outer
// hairline border was dropped per design tweak so the popup blends into the
// blurred backdrop without a visible frame.
private const val MODAL_BG = "#09142d"
private const val TRACK_BG = "#818d9c"

// How long each story stays on screen before auto-advancing. Matches the
// Instagram-style ~5s cadence the design implies (the Figma progress bar's
// partial-fill ratio reads as roughly one-fifth of the duration).
private const val STORY_DURATION_MS = 5_000L
// Update cadence for the progress-bar fill animation — 60fps is overkill for
// a 5s sweep and would burn extra recompositions, so we tick every ~50ms.
private const val PROGRESS_TICK_MS = 50L
// Horizontal pointer travel (CSS px) past which a press is treated as a swipe
// rather than a hold-to-pause. Below the threshold we resume on release; at
// or above it we commit a prev/next navigation in the swipe direction.
private const val SWIPE_THRESHOLD_PX = 40.0

@Composable
fun StoryViewerModal(
    stories: List<UserStoryDto>,
    initialIndex: Int,
    title: String,
    sportName: String,
    activeCard: UserCardDto?,
    avatarUrl: String?,
    onDismiss: () -> Unit,
    onLikeClick: ((storyId: String) -> Unit)? = null,
) {
    if (stories.isEmpty()) return
    Box(
        modifier = Modifier
            .position(Position.Fixed)
            .top(0.px).left(0.px).right(0.px).bottom(0.px)
            .zIndex(1100)
            .styleModifier {
                property("background", "rgba(0, 0, 0, 0.6)")
                property("backdrop-filter", "blur(4px)")
                property("-webkit-backdrop-filter", "blur(4px)")
            }
            .onClick { onDismiss() },
        contentAlignment = Alignment.Center,
    ) {
        // Outer card — Figma dimensions 450×… (padding 16/16/28/16, gap 24).
        Column(
            modifier = Modifier
                .sMaxWidth(450)
                .fillMaxWidth()
                .sMargin(16)
                .onClick { it.stopPropagation() }
                .styleModifier {
                    property("background", MODAL_BG)
                    property("border-radius", s(24))
                    property("padding", "${s(16)} ${s(16)} ${s(28)} ${s(16)}")
                    property("box-sizing", "border-box")
                    property("overflow", "hidden")
                },
        ) {
            StoryViewerContent(
                stories = stories,
                initialIndex = initialIndex,
                title = title,
                sportName = sportName,
                activeCard = activeCard,
                avatarUrl = avatarUrl,
                onDismiss = onDismiss,
                onLikeClick = onLikeClick,
                showClose = true,
            )
        }
    }
}

/**
 * Reusable story-viewer body — the auto-advance state machine plus the
 * segmented progress bar, identity row, swipeable image and footer counters,
 * with no surrounding surface of its own. [StoryViewerModal] wraps it in a
 * centred desktop card; the mobile profile wraps it in the shared bottom sheet
 * ([com.arena_web.ui.sections.profile.mobile.MobileStoryViewerSheet], Figma
 * 8682:456159).
 *
 * [showClose] renders the in-header close disc used by the desktop modal. The
 * bottom sheet passes `false` — it dismisses via the sheet's own drag / scrim.
 */
@Composable
fun StoryViewerContent(
    stories: List<UserStoryDto>,
    initialIndex: Int,
    title: String,
    sportName: String,
    activeCard: UserCardDto?,
    avatarUrl: String?,
    onDismiss: () -> Unit,
    onLikeClick: ((storyId: String) -> Unit)? = null,
    showClose: Boolean = true,
    // When set, the "Seen by" pill becomes tappable and invokes this with the
    // current story id, so the caller can pop the "Who saw this story?" sheet
    // (Figma 8682:456190) over this one.
    onSeenByClick: ((storyId: String) -> Unit)? = null,
    // True while that viewers sheet is open — holds auto-advance like a press.
    externallyPaused: Boolean = false,
) {
    if (stories.isEmpty()) return
    val safeInitial = initialIndex.coerceIn(0, stories.lastIndex)
    // Re-key on the underlying story id list so reopening with a refreshed
    // list resets navigation correctly.
    var index by remember(stories.map { it.id }) { mutableStateOf(safeInitial) }
    val current = stories.getOrNull(index) ?: return

    // Auto-advance timer. `progress` runs 0f → 1f over STORY_DURATION_MS for
    // the current story. Single LaunchedEffect handles BOTH the reset on a
    // story change and the timer tick — keeping them apart caused a visible
    // "snap back to 0" because Compose can interleave concurrent effects.
    // `lastTimedStoryId` lets us decide whether to start fresh (new story) or
    // resume from the saved progress (release after hold).
    var progress by remember { mutableStateOf(0f) }
    var paused by remember { mutableStateOf(false) }
    var lastTimedStoryId by remember { mutableStateOf("") }

    LaunchedEffect(current.id, paused, externallyPaused) {
        // Fresh story → start the segment at 0.
        if (lastTimedStoryId != current.id) {
            progress = 0f
            lastTimedStoryId = current.id
        }
        // Hold the timer while the viewers sheet is open, like a press-hold.
        if (paused || externallyPaused) return@LaunchedEffect
        val resumeStartedAt = kotlin.js.Date().getTime()
        val baseProgress = progress
        while (true) {
            delay(PROGRESS_TICK_MS)
            val elapsedSinceResume = kotlin.js.Date().getTime() - resumeStartedAt
            val ratio = (baseProgress + (elapsedSinceResume.toDouble() / STORY_DURATION_MS))
                .toFloat()
                .coerceIn(0f, 1f)
            progress = ratio
            if (ratio >= 1f) {
                if (index < stories.lastIndex) {
                    index += 1
                } else {
                    onDismiss()
                }
                break
            }
        }
    }

    val goPrev = {
        if (index > 0) index -= 1 else progress = 0f
    }
    val goNext = { if (index < stories.lastIndex) index += 1 else onDismiss() }

    Column(modifier = Modifier.fillMaxWidth().sGap(24)) {
        // Header column: progress bar + identity row.
        Column(modifier = Modifier.fillMaxWidth().sGap(16)) {
            ProgressBar(
                total = stories.size,
                currentIndex = index,
                activeProgress = progress,
            )
            IdentityRow(
                title = title,
                relativeTime = formatStoryAge(current.createdAt),
                sportName = sportName,
                activeCard = activeCard,
                avatarUrl = avatarUrl,
                onClose = onDismiss,
                showClose = showClose,
            )
        }

        // Image area — Figma 378×380, rounded 24, hairline white border.
        // Pointer interactions:
        //   • hold        → pause auto-advance
        //   • release     → resume
        //   • swipe ±X    → goPrev / goNext (commits on threshold)
        StoryImage(
            storyId = current.id,
            imageUrl = current.imageUrl,
            onHold = { paused = true },
            onRelease = { paused = false },
            onSwipePrev = {
                paused = false
                goPrev()
            },
            onSwipeNext = {
                paused = false
                goNext()
            },
        )

        // Footer counters — real view + like state from the story payload.
        // The Seen-by pill pops the viewers sheet (when wired); the heart fires
        // onLikeClick (optimistic update lives in the caller).
        FooterCounters(
            seenCount = current.viewsCount.toInt(),
            likesCount = current.likesCount.toInt(),
            isLikedByMe = current.isLikedByMe,
            onLikeClick = onLikeClick?.let { cb -> { cb(current.id) } },
            onSeenByClick = onSeenByClick?.let { cb -> { cb(current.id) } },
        )
    }
}

@Composable
private fun ProgressBar(total: Int, currentIndex: Int, activeProgress: Float) {
    // Segmented Instagram-style bar — one segment per story with a small gap
    // between them. Already-watched segments are fully white, the active one
    // fills left→right as the 5s timer advances, and upcoming segments stay
    // on the muted track colour from Figma.
    Row(
        modifier = Modifier.fillMaxWidth().sGap(4),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        repeat(total.coerceAtLeast(1)) { i ->
            val fillPercent = when {
                i < currentIndex -> 100f
                i == currentIndex -> (activeProgress.coerceIn(0f, 1f) * 100f)
                else -> 0f
            }
            Box(
                modifier = Modifier
                    .flexGrow(1)
                    .sHeight(4)
                    .sBorderRadius(100)
                    .styleModifier {
                        property("background", TRACK_BG)
                        property("position", "relative")
                        property("overflow", "hidden")
                    },
            ) {
                // Inner fill bar — width animates from 0% → 100% via the
                // outer LaunchedEffect ticking `activeProgress` every ~50ms.
                Box(
                    modifier = Modifier
                        .fillMaxHeight()
                        .sBorderRadius(100)
                        .styleModifier {
                            property("width", "$fillPercent%")
                            property("background", "#ffffff")
                            property("transition", "width ${PROGRESS_TICK_MS}ms linear")
                        },
                )
            }
        }
    }
}

@Composable
private fun IdentityRow(
    title: String,
    relativeTime: String,
    sportName: String,
    activeCard: UserCardDto?,
    avatarUrl: String?,
    onClose: () -> Unit,
    showClose: Boolean = true,
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        // Left: mini player card + label/time stack. The card is the same
        // Highlight (37×58) variant used in the comments list so the visual
        // language stays consistent across modals.
        Row(
            modifier = Modifier.sGap(22).flexGrow(1),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Box(modifier = Modifier.styleModifier { property("flex-shrink", "0") }) {
                if (activeCard != null) {
                    // Mirrors PostListCard's avatar resolution: the card's
                    // AI-generated slot is the only legitimate source — the
                    // user's Google OAuth avatar (userAvatarUrl) would punch
                    // through the player-card silhouette and is intentionally
                    // skipped. Empty-avatar placeholder fills the gap when
                    // the card slot hasn't been generated yet.
                    PlayerCard(
                        playerName = "",
                        rating = 0,
                        position = "",
                        sport = sportName,
                        size = PlayerCardSize.Highlight,
                        cardFrameUrl = activeCard.cardImageUrl,
                        cardAssetsBasePath = activeCard.assetsBasePath,
                        cardTextColor = activeCard.textColor,
                        avatarUrl = activeCard.avatarUrl ?: AppIcons.EmptyAvatar,
                        hideRating = true,
                        hideShadow = true,
                    )
                } else {
                    // Avatar fallback — circular bust shot when the user has
                    // no active card to show.
                    Box(
                        modifier = Modifier
                            .sSize(48)
                            .sBorderRadius(100)
                            .styleModifier {
                                property("background-color", "#1A2238")
                                if (avatarUrl != null) {
                                    property("background-image", "url('$avatarUrl')")
                                    property("background-size", "cover")
                                    property("background-position", "center")
                                }
                            },
                    )
                }
            }
            Row(
                modifier = Modifier.sGap(22),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                SpanText(
                    title,
                    modifier = Modifier
                        .sFontSize(20)
                        .fontWeight(AppTypography.Medium)
                        .sLineHeight(26)
                        .sLetterSpacing(0.2)
                        .color(AppColors.Text.Secondary),
                )
                SpanText(
                    relativeTime,
                    modifier = Modifier
                        .sFontSize(16)
                        .fontWeight(AppTypography.Light)
                        .sLineHeight(24)
                        .sLetterSpacing(0.032)
                        .color(AppColors.Text.Secondary),
                )
            }
        }
        // Right: close button — 44×44 reservation, 36×36 disc. Uses the
        // shared GradientBorderBox so the hairline is rendered as an
        // absolutely-positioned mask-composite overlay (same approach as
        // category chips / PersonDetailDialog buttons) rather than a flat
        // alpha border that would have to bake into the background paint.
        // Hidden in the bottom-sheet variant, which dismisses via drag / scrim.
        if (showClose) {
            Box(
                modifier = Modifier.sSize(44),
                contentAlignment = Alignment.Center,
            ) {
                GradientBorderBox(
                    modifier = Modifier
                        .sSize(36)
                        .cursor(Cursor.Pointer)
                        .onClick {
                            it.stopPropagation()
                            onClose()
                        },
                    borderRadius = 100,
                    background = AppGradients.InteractiveSecondary,
                ) {
                    Icon(src = AppIcons.Close, modifier = Modifier.sSize(14))
                }
            }
        }
    }
}

@Composable
private fun StoryImage(
    storyId: String,
    imageUrl: String,
    onHold: () -> Unit,
    onRelease: () -> Unit,
    onSwipePrev: () -> Unit,
    onSwipeNext: () -> Unit,
) {
    // Stable DOM id so the DisposableEffect below can attach pointer listeners
    // to the same element across recompositions. Suffix with story id so a
    // remounting story still gets fresh handlers (and pointer capture state
    // doesn't leak between previews).
    val elementId = "story-viewer-surface-$storyId"

    // Pointer-event wiring: down = pause + remember start X, move = track
    // current dx, up = decide swipe (|dx| ≥ threshold) vs resume. Pointer
    // events cover both mouse and touch in one path.
    DisposableEffect(storyId) {
        val el = document.getElementById(elementId)
        if (el == null) {
            onDispose {}
        } else {
            // `isPressed` gates pointermove — otherwise plain hover (with no
            // prior pointerdown) treats `downX = 0` as the press origin and
            // any cursor movement past 40px synthesises a swipe. `didSwipe`
            // makes sure we only fire the navigation callback once per gesture.
            var isPressed = false
            var downX = 0.0
            var didSwipe = false
            val downHandler: (Event) -> Unit = handler@{ event ->
                val pe = event.asDynamic()
                downX = (pe.clientX as Number).toDouble()
                didSwipe = false
                isPressed = true
                el.asDynamic().setPointerCapture(pe.pointerId)
                onHold()
            }
            val moveHandler: (Event) -> Unit = handler@{ event ->
                if (!isPressed || didSwipe) return@handler
                val pe = event.asDynamic()
                val dx = (pe.clientX as Number).toDouble() - downX
                if (kotlin.math.abs(dx) >= SWIPE_THRESHOLD_PX) {
                    didSwipe = true
                    isPressed = false
                    if (dx < 0) onSwipeNext() else onSwipePrev()
                }
            }
            val upHandler: (Event) -> Unit = handler@{ _ ->
                if (!isPressed) return@handler
                isPressed = false
                if (!didSwipe) {
                    // Plain tap/hold release — resume playback.
                    onRelease()
                }
            }
            val cancelHandler: (Event) -> Unit = handler@{ _ ->
                if (!isPressed) return@handler
                isPressed = false
                // Browser cancelled the pointer (e.g. window blur). Treat as
                // a release so the story doesn't stay paused forever.
                if (!didSwipe) onRelease()
            }
            el.addEventListener("pointerdown", downHandler)
            el.addEventListener("pointermove", moveHandler)
            el.addEventListener("pointerup", upHandler)
            el.addEventListener("pointercancel", cancelHandler)
            onDispose {
                el.removeEventListener("pointerdown", downHandler)
                el.removeEventListener("pointermove", moveHandler)
                el.removeEventListener("pointerup", upHandler)
                el.removeEventListener("pointercancel", cancelHandler)
            }
        }
    }

    // The frame keeps the 378×380 Figma aspect ratio while scaling down on
    // narrower viewports — width is capped at 378 and the height matches.
    Box(
        modifier = Modifier.fillMaxWidth(),
        contentAlignment = Alignment.Center,
    ) {
        // GradientBorderBox draws the photo as the wrapper background and
        // overlays the ButtonBorderGradientPrimary hairline via mask-composite
        // — same approach as every other bordered surface in the app, so the
        // image paint never mixes with the border gradient.
        GradientBorderBox(
            modifier = Modifier
                .sMaxWidth(378)
                .fillMaxWidth()
                .id(elementId)
                .styleModifier {
                    property("aspect-ratio", "378 / 380")
                    property("overflow", "hidden")
                    // Disable native horizontal pan + selection so the swipe
                    // gesture isn't hijacked by the browser on touch devices.
                    property("touch-action", "pan-y")
                    property("user-select", "none")
                    property("-webkit-user-select", "none")
                    property("cursor", "pointer")
                },
            borderRadius = 24,
            background = "url('$imageUrl') center / cover no-repeat",
            borderWidth = "1px",
        ) {
            // No tap-zone children — pointer events on the wrapper element
            // are handled by the DisposableEffect above.
        }
    }
}

@Composable
private fun FooterCounters(
    seenCount: Int,
    likesCount: Int,
    isLikedByMe: Boolean,
    onLikeClick: (() -> Unit)?,
    onSeenByClick: (() -> Unit)? = null,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier { property("padding", "0 ${s(20)}") },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        CounterPill(
            iconSrc = AppIcons.Eye,
            iconSize = 24,
            text = LocalAppStrings.current.storySeenBy(seenCount),
            onClick = onSeenByClick,
            tinted = false,
            fixedTextWidth = null,
        )
        Box(modifier = Modifier.flexGrow(1))
        CounterPill(
            iconSrc = AppIcons.Heart,
            iconSize = 16,
            text = "$likesCount",
            onClick = onLikeClick,
            tinted = isLikedByMe,
            // null → dynamic width with a one-digit "headroom" reserved so a
            // single +1 like (e.g. 9→10) doesn't snap the pill outward.
            fixedTextWidth = null,
        )
    }
}

@Composable
private fun CounterPill(
    iconSrc: String,
    iconSize: Int,
    text: String,
    onClick: (() -> Unit)?,
    tinted: Boolean,
    fixedTextWidth: Int?,
) {
    // Same shared gradient-border widget as the close button and category
    // chips. Padding lives on an inner Row, because GradientBorderBox draws
    // its overlay against the wrapper's full silhouette — padding on the
    // wrapper would shrink the overlay and clip the border on every edge
    // (the gotcha called out in the GradientBorderBox docs).
    GradientBorderBox(
        borderRadius = 100,
        background = AppGradients.InteractiveSecondary,
    ) {
        Row(
            modifier = Modifier
                .sGap(12)
                .styleModifier { property("padding", "${s(6)} ${s(12)}") }
                .then(
                    if (onClick != null) Modifier.cursor(Cursor.Pointer).onClick { onClick() }
                    else Modifier
                ),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Box(
                modifier = Modifier
                    .sSize(iconSize)
                    .styleModifier {
                        // Lock the icon onto its own GPU layer so toggling
                        // `filter` can't nudge it on the subpixel grid.
                        property("transform", "translateZ(0)")
                        property("flex-shrink", "0")
                    },
                contentAlignment = Alignment.Center,
            ) {
                Icon(
                    src = iconSrc,
                    modifier = Modifier
                        .sSize(iconSize)
                        .then(
                            if (tinted) Modifier.styleModifier {
                                // Same red as the post heart's liked state, so the
                                // affordance reads consistently across surfaces.
                                property(
                                    "filter",
                                    "brightness(0) saturate(100%) invert(27%) sepia(95%) saturate(4997%) hue-rotate(344deg) brightness(95%) contrast(99%)",
                                )
                            } else Modifier
                        ),
                )
            }
            SpanText(
                text,
                modifier = Modifier
                    .sFontSize(14)
                    .fontWeight(AppTypography.Regular)
                    .sLineHeight(22)
                    .sLetterSpacing(0.028)
                    .color(AppColors.Text.Secondary)
                    .styleModifier {
                        if (fixedTextWidth != null) {
                            // Hard-reserve a digit slot so the pill width
                            // never shifts as the count changes.
                            property("width", s(fixedTextWidth))
                            property("flex-shrink", "0")
                            property("text-align", "right")
                            property("font-variant-numeric", "tabular-nums")
                        } else if (text.all { it.isDigit() }) {
                            // Dynamic numeric field — reserve one extra digit
                            // of headroom so a +1 like that crosses a digit
                            // boundary (9→10, 99→100, 999→1000) absorbs
                            // smoothly inside the already-reserved space
                            // instead of snapping the pill wider.
                            property("min-width", "${text.length + 1}ch")
                            property("flex-shrink", "0")
                            property("text-align", "right")
                            property("font-variant-numeric", "tabular-nums")
                        }
                    },
            )
        }
    }
}

// "5min" / "2h" / "3d" — compact form (Figma shows "25min").
private fun formatStoryAge(isoDate: String): String = try {
    val date = kotlin.js.Date(isoDate)
    val diffMs = kotlin.js.Date().getTime() - date.getTime()
    val diffMin = (diffMs / 60000).toLong().coerceAtLeast(0)
    val diffHours = diffMin / 60
    val diffDays = diffHours / 24
    when {
        diffMin < 1 -> "now"
        diffMin < 60 -> "${diffMin}min"
        diffHours < 24 -> "${diffHours}h"
        else -> "${diffDays}d"
    }
} catch (_: Throwable) {
    ""
}
